
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { SparklesIcon } from '@heroicons/react/outline';
import { GetStaticPropsContext } from 'next';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect, useState, useCallback, memo, useRef } from 'react';
import { Image as ImageType } from 'types';
import { similaritySearchImagesV2 } from '@/data/images';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import { HAS_FREE_PLAN } from '@/constants/common';
import { imageTools, ImageTool } from '@/constants/imageTools';
import { imageToolShowcases } from '@/constants/imageToolsShowcases';
import { getEmbeddingVector } from '@/utils/embeddings';
import ImageTransformationShowcase from '@/components/landing/ImageTools/ImageTransformationShowcase';
import HappyUsers from '@/components/landing/HappyUsers';
// Dynamically load heavy components
const UserImages = dynamic(() => import('@/components/images/UserImages'), {
    ssr: false,
    loading: () => (<div className='py-16 flex justify-center'>
      <div className='animate-pulse flex space-x-4'>
        <div className='rounded-lg bg-gray-200 h-32 w-32'></div>
        <div className='rounded-lg bg-gray-200 h-32 w-32'></div>
        <div className='rounded-lg bg-gray-200 h-32 w-32'></div>
      </div>
    </div>),
});
const UserImagesStatic = dynamic(() => import('@/components/images/UserImagesStatic'), {
    ssr: false,
    loading: () => (<div className='py-16 flex justify-center'>
        <div className='animate-pulse flex space-x-4'>
          <div className='rounded-lg bg-gray-200 h-32 w-32'></div>
          <div className='rounded-lg bg-gray-200 h-32 w-32'></div>
          <div className='rounded-lg bg-gray-200 h-32 w-32'></div>
        </div>
      </div>),
});
const ImageStyleUseCases = dynamic(() => import('@/components/ImageStyleUseCases'), {
    ssr: false,
});
interface ImageToolPageProps {
    tool: ImageTool;
    images: ImageType[];
}
const ImageToolPage = ({ tool, images }: ImageToolPageProps) => {
    const { t, lang } = useLocale('ai-image-generator');
    const [showSticky, setShowSticky] = useState(false);
    const [showUserImages, setShowUserImages] = useState(false);
    const [showImageShowcase, setShowImageShowcase] = useState(false);
    const userImagesRef = useRef<HTMLDivElement>(null);
    const showcaseRef = useRef<HTMLDivElement>(null);
    // Get showcase data if available for this tool
    const showcaseData = tool.slug ? imageToolShowcases[tool.slug] : null;
    // Memoize components to prevent unnecessary renders
    const MemoizedUserImages = useCallback(() => (<UserImages initialImages={images} currentImage='0' searchQuery={tool.title}/>), [images, tool.title]);
    const MemoizedUserImagesStatic = useCallback(() => <UserImagesStatic initialImages={images}/>, [images]);
    // Throttled scroll handler for sticky CTA
    useEffect(() => {
        // Throttling function to limit how often the scroll handler fires
        const throttle = (callback: Function, delay: number) => {
            let lastCall = 0;
            return function (...args: any[]) {
                const now = new Date().getTime();
                if (now - lastCall >= delay) {
                    lastCall = now;
                    callback(...args);
                }
            };
        };
        const handleScroll = throttle(() => {
            // Show sticky section after scrolling 2 viewport heights
            const scrollThreshold = window.innerHeight * 1.3;
            const shouldShow = window.scrollY > scrollThreshold;
            // Only update state if the value is changing
            if (shouldShow !== showSticky) {
                setShowSticky(shouldShow);
            }
        }, 100); // Throttle to run max once every 100ms
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [showSticky]);
    // Intersection Observer for UserImages section
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setShowUserImages(true);
                observer.disconnect();
            }
        }, {
            rootMargin: '200px 0px', // Start loading when within 200px of viewport
            threshold: 0.1,
        });
        if (userImagesRef.current) {
            observer.observe(userImagesRef.current);
        }
        return () => observer.disconnect();
    }, []);
    // Intersection Observer for Showcase section
    useEffect(() => {
        if (showcaseData) {
            const observer = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting) {
                    setShowImageShowcase(true);
                    observer.disconnect();
                }
            }, {
                rootMargin: '200px 0px', // Start loading when within 200px of viewport
                threshold: 0.1,
            });
            if (showcaseRef.current) {
                observer.observe(showcaseRef.current);
            }
            return () => observer.disconnect();
        }
    }, [showcaseData]);
    // Extract first image for display (or use a default if not available)
    const mainImage = tool.image || '/img/tools/default.jpg';
    const isVideo = tool.isVideoPreview;
    return (<main className='relative bg-gray-100 dark:bg-slate-900'>
      <Seo templateTitle={t(tool.seo_title as string, {}, { ns: 'image-tools' }) ||
            `${tool.title} - Easy-Peasy.ai`} description={t(tool.seo_description as string, {}, { ns: 'image-tools' }) ||
            tool.description} image={!isVideo
            ? mainImage
            : `https://easy-peasy.ai/api/og-template?title=${tool.title}`}/>

      <LandingPageNavigation skipUserContext/>

      <main>
        <div className='relative isolate'>
          <svg className='absolute inset-x-0 top-0 -z-10 h-[64rem] w-full stroke-gray-200 [mask-image:radial-gradient(32rem_32rem_at_center,white,transparent)] dark:stroke-gray-800' aria-hidden='true'>
            <defs>
              <pattern id='1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84' width={200} height={200} x='50%' y={-1} patternUnits='userSpaceOnUse'>
                <path d='M.5 200V.5H200' fill='none'/>
              </pattern>
            </defs>
            <svg x='50%' y={-1} className='overflow-visible fill-gray-50 dark:hidden'>
              <path d='M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z' strokeWidth={0}/>
            </svg>
            <rect width='100%' height='100%' strokeWidth={0} fill='url(#1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84)'/>
          </svg>
          <div className='absolute left-1/2 right-0 top-0 -z-10 -ml-24 transform-gpu overflow-hidden blur-3xl lg:ml-24 xl:ml-48' aria-hidden='true'>
            <div className='aspect-[801/1036] w-[50.0625rem] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30' style={{
            clipPath: 'polygon(63.1% 29.5%, 100% 17.1%, 76.6% 3%, 48.4% 0%, 44.6% 4.7%, 54.5% 25.3%, 59.8% 49%, 55.2% 57.8%, 44.4% 57.2%, 27.8% 47.9%, 35.1% 81.5%, 0% 97.7%, 39.2% 100%, 35.2% 81.4%, 97.2% 52.8%, 63.1% 29.5%)',
        }}/>
          </div>
          <div className='overflow-hidden'>
            <div className='mx-auto max-w-7xl px-6 pb-32 pt-36 sm:pt-60 lg:px-8 lg:pt-32'>
              <div className='mx-auto max-w-2xl gap-x-14 lg:mx-0 lg:flex lg:max-w-none lg:items-center'>
                <div className='w-full max-w-xl lg:shrink-0 xl:max-w-xl'>
                  <h1 className='text-4xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-5xl'>
                    {t(tool.title as string, {}, { ns: 'image-tools' })}
                  </h1>
                  <p className='relative mt-6 hidden text-lg leading-8 text-gray-600 dark:text-slate-300 sm:block sm:max-w-md lg:max-w-none'>
                    {t(tool.description as string, {}, { ns: 'image-tools' })}
                  </p>
                  <div className='mt-10 flex items-center gap-x-6'>
                    <ButtonLink href={HAS_FREE_PLAN
            ? `/login?preset=${tool.uri.split('?')[0].substring(1)}`
            : '/#pricing'} variant='dark'>
                      {t('Start Creating Now')}
                    </ButtonLink>
                  </div>
                  <HappyUsers />
                </div>
                <div className='mt-14 flex justify-end gap-8 sm:-mt-44 sm:pl-20 lg:mt-0 lg:pl-0'>
                  <div className='relative ml-auto flex-none w-80 xl:w-[32rem]'>
                    {!isVideo ? (<Image width='600' height='600' src={mainImage} alt={tool.title} className='w-full rounded-xl bg-gray-900/5 object-cover shadow-lg' priority loading='eager'/>) : (<video autoPlay loop muted playsInline className='w-full rounded-xl bg-gray-900/5 object-cover shadow-lg' preload='metadata'>
                        <source src={mainImage} type='video/mp4'/>
                      </video>)}
                    <div className='pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10'/>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <LogosCloud />

      {/* Image Transformation Showcase */}
      {showcaseData && (<div ref={showcaseRef} className='min-h-[300px]'>
          {showImageShowcase && (<ImageTransformationShowcase title={showcaseData.title} description={showcaseData.description} examples={showcaseData.examples}/>)}
        </div>)}

      {lang === 'en' && tool.slug && (<ImageStyleUseCases style={{ slug: tool.slug }}/>)}
      <div ref={userImagesRef} className='min-h-[300px] relative bg-white dark:bg-slate-800'>
        {!showUserImages && (<div className='py-12'>
            <div className='mx-auto max-w-7xl px-6 lg:px-8'>
              <div className='text-center'>
                <h2 className='text-3xl font-bold tracking-tight text-primary-600 sm:text-4xl mb-12'>
                  {t('Examples of Generated Art & Images')}
                </h2>
                <div className='animate-pulse flex flex-wrap justify-center gap-6'>
                  {[...Array(6)].map((_, i) => (<div key={i} className='rounded-lg bg-gray-200 dark:bg-gray-700 h-64 w-64'></div>))}
                </div>
              </div>
            </div>
          </div>)}
        {showUserImages && (<>
            {lang === 'en' ? (<MemoizedUserImages />) : (<MemoizedUserImagesStatic />)}
          </>)}
      </div>
      <FeaturedLogos />
      <Testimonials />
      <CallToAction preset={tool.uri.split('?')[0].substring(1)}/>

      {/* Sticky CTA Section */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 ${showSticky ? 'translate-y-0' : 'translate-y-full'} will-change-transform transition-transform duration-300 ease-in-out`}>
        <div className='border-t border-gray-200 bg-white/95 shadow-lg backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800/95'>
          <div className='mx-auto max-w-4xl px-4 py-3 sm:px-6 lg:px-8'>
            <div className='flex flex-col items-center gap-4 sm:flex-row sm:justify-between'>
              <div className='hidden sm:block'>
                {!isVideo ? (<Image src={mainImage} alt={tool.title} width={88} height={88} className='rounded-lg object-cover'/>) : (<div className='rounded-lg bg-gray-200 h-[88px] w-[88px] flex items-center justify-center'>
                    <SparklesIcon className='h-8 w-8 text-gray-500'/>
                  </div>)}
              </div>
              <div className='flex-1 text-center sm:mx-6 sm:text-left'>
                <h2 className='text-lg font-semibold text-gray-900 dark:text-slate-200 sm:text-xl'>
                  {t(tool.title as string, {}, { ns: 'image-tools' })}
                </h2>
                <p className='mt-1 text-sm text-gray-600 dark:text-slate-400'>
                  {t(tool.description as string, {}, { ns: 'image-tools' })}
                </p>
              </div>
              <div className='w-full flex-shrink-0 sm:w-auto'>
                <ButtonLink href={HAS_FREE_PLAN
            ? `/login?preset=${tool.uri.split('?')[0].substring(1)}`
            : '/#pricing'} variant='dark' className='w-full justify-center sm:w-auto'>
                  <SparklesIcon className='mr-2 h-5 w-5'/>
                  {t('Start Creating Now')}
                </ButtonLink>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </main>);
};
export default memo(ImageToolPage);
export async function getStaticPaths() {
    const locales = [
        'en',
        'ja',
        'es',
        'fr',
        'it',
        'de',
        'ko',
        'zh',
        'ar',
        'th',
        'pt',
    ];
    const paths = imageTools
        .filter((tool) => !tool.skipStaticPaths)
        .flatMap((tool) => locales.map((locale) => ({
        params: { slug: tool.slug },
        locale,
    })));
    return {
        paths,
        fallback: false,
    };
}
const getStaticProps = async (ctx: GetStaticPropsContext) => {
    const slug = ctx?.params?.slug as string;
    const toolInfo = imageTools.find((t) => t.slug === slug);
    if (!toolInfo) {
        return {
            notFound: true,
        };
    }
    let images: Partial<ImageType>[] = [];
    // get search embedding
    const embedding = await getEmbeddingVector(toolInfo.title, 'text-embedding-3-small');
    // if image has embedding, get similar images
    images = await similaritySearchImagesV2(0, embedding, 1);
    return {
        props: {
            tool: toolInfo,
            images,
        },
    };
};

    async function __Next_Translate__getStaticProps__1a12e86ef2a__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/image-tools/[slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a12e86ef2a__ as getStaticProps }
  